import { Clock, Pin } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import type { ReminderItem } from '../../../hooks/use-reminders'
import { TodoTag, TodoTags } from '../../cells/todo-tags'
import { useDueSoon } from '../hooks/use-due-soon'
import { formatDatePill, type DueState } from '../utils/due-date'
import { isPinnedReminder } from '../utils/pinned'
import { usePreferredHourCycle } from '../utils/user-time-format'

// No crimson token yet: the danger tone is an orange-leaning red, and a to-do's
// overdue date asked for dark crimson. TodoMark's overdue tone uses the same inks,
// so keep the two in step.
const OVERDUE_CRIMSON = 'text-[oklch(45%_0.17_15)] dark:text-[oklch(82%_0.1_12)]'

/** Whether a reminder has anything for the meta row to say. */
export function hasReminderMeta(item: ReminderItem): boolean {
  return item.scheduledAt != null || isPinnedReminder(item)
}

/**
 * Due date and pin under the title of a collapsed row. Those rows hide the
 * source row (and with it the interactive due-date pill), so this is where the
 * two facts a reminder is scheduled or pinned live — read-only, since editing
 * them belongs to the hover card and the Reminder View.
 */
export function ReminderMetaRow({
  item,
  resolved,
  className,
}: {
  item: ReminderItem
  resolved: boolean
  className?: string
}) {
  const hourCycle = usePreferredHourCycle()
  const dueSoon = useDueSoon(item.scheduledAt)

  return (
    <div
      className={twMerge(
        'text-foreground-secondary type-ui-xs flex w-full flex-wrap items-center gap-x-2 gap-y-1 pl-7 font-medium',
        className,
      )}
    >
      {item.scheduledAt && (
        <span
          className={twMerge('flex shrink-0 items-center gap-1', !resolved && dueSoon && 'text-danger-subtle')}
          aria-label="Due date"
        >
          <Clock className="size-3" strokeWidth={2} />
          {formatDatePill(item.scheduledAt, hourCycle)}
        </span>
      )}
      {isPinnedReminder(item) && (
        <span className="flex shrink-0 items-center gap-1">
          <Pin className="size-3" strokeWidth={2} />
          Pinned
        </span>
      )}
    </div>
  )
}

/** A To-dos row's tags: the due date, in crimson once it's overdue. */
export function ReminderTags({ item, resolved, due }: { item: ReminderItem; resolved: boolean; due?: DueState }) {
  const hourCycle = usePreferredHourCycle()
  const overdue = due === 'overdue' && !resolved
  if (!item.scheduledAt) return null

  // No pin: a pinned reminder already sits under the Pinned heading.
  return (
    <TodoTags>
      <TodoTag
        label={overdue ? `Overdue: ${formatDatePill(item.scheduledAt, hourCycle)}` : undefined}
        className={overdue ? OVERDUE_CRIMSON : undefined}
      >
        {formatDatePill(item.scheduledAt, hourCycle)}
      </TodoTag>
    </TodoTags>
  )
}
